// File: client/src/components/marketing/roadmap/calendar/editors/SocialPostEditor.js
// Professional Content Studio - Split Panel Design with Advanced Image Generation

import React, { useState, useEffect, useRef } from 'react';
import { businessAPI } from '../../../../../services/businessAPI';
import { STATUS_CONFIG, STATUS_FLOW } from '../DayCard';
import DesignStudio from '../../DesignStudio';
import ImageSourcePicker from '../../ImageSourcePicker';
import AIImageGenerator from '../../AIImageGenerator';

// Platform configuration
const PLATFORM_CONFIG = {
  instagram: { icon: 'fab fa-instagram', name: 'Instagram', color: '#E4405F', maxCaption: 2200, hookLimit: 125, hashtagMax: 30 },
  facebook: { icon: 'fab fa-facebook', name: 'Facebook', color: '#1877F2', maxCaption: 63206, hookLimit: 300, hashtagMax: 3 },
  linkedin: { icon: 'fab fa-linkedin', name: 'LinkedIn', color: '#0A66C2', maxCaption: 3000, hookLimit: 210, hashtagMax: 5 },
  twitter: { icon: 'fab fa-twitter', name: 'Twitter/X', color: '#1DA1F2', maxCaption: 280, hookLimit: 280, hashtagMax: 5 },
  tiktok: { icon: 'fab fa-tiktok', name: 'TikTok', color: '#000000', maxCaption: 2200, hookLimit: 150, hashtagMax: 5 },
  youtube: { icon: 'fab fa-youtube', name: 'YouTube', color: '#FF0000', maxCaption: 5000, hookLimit: 100, hashtagMax: 5 }
};

const CONTENT_TYPES = {
  educational: { label: 'Educational', color: '#3B82F6' },
  entertainment: { label: 'Entertainment', color: '#8B5CF6' },
  inspirational: { label: 'Inspirational', color: '#F59E0B' },
  promotional: { label: 'Promotional', color: '#10B981' }
};

const TONE_OPTIONS = [
  { id: 'empathetic', label: 'Empathetic', desc: 'Warm and understanding' },
  { id: 'humorous', label: 'Humorous', desc: 'Fun and lighthearted' },
  { id: 'professional', label: 'Professional', desc: 'Authoritative' },
  { id: 'inspirational', label: 'Inspirational', desc: 'Motivating' },
  { id: 'educational', label: 'Educational', desc: 'Informative' },
  { id: 'casual', label: 'Casual', desc: 'Conversational' }
];

const FORMAT_OPTIONS = [
  { id: 'standard', label: 'Post', icon: 'fas fa-align-left' },
  { id: 'story', label: 'Story', icon: 'fas fa-mobile-alt' }
];

// Strip AI markdown artifacts (**, *, ##, `) from generated text before display
const stripMarkdown = (text) => {
  if (!text) return text;
  return text
    .replace(/\*\*/g, '')   // bold markers
    .replace(/\*/g, '')      // italic / bullet markers
    .replace(/^#{1,6}\s/gm, '') // headings
    .replace(/`/g, '');      // code backticks
};

// Colour theory presets for AI image generation
const COLOUR_HARMONIES = {
  complementary: { label: 'Complementary', desc: 'High contrast, vibrant', icon: 'fas fa-circle-half-stroke' },
  analogous: { label: 'Analogous', desc: 'Harmonious, cohesive', icon: 'fas fa-palette' },
  triadic: { label: 'Triadic', desc: 'Balanced, dynamic', icon: 'fas fa-draw-polygon' },
  monochromatic: { label: 'Monochromatic', desc: 'Clean, elegant', icon: 'fas fa-droplet' }
};

const IMAGE_STYLES = [
  { id: 'photorealistic', label: 'Photorealistic', desc: 'Ultra-realistic photo quality' },
  { id: 'editorial', label: 'Editorial', desc: 'Magazine-style photography' },
  { id: 'lifestyle', label: 'Lifestyle', desc: 'Natural, candid feel' },
  { id: 'minimal', label: 'Minimal', desc: 'Clean and simple' },
  { id: 'cinematic', label: 'Cinematic', desc: 'Film-like colour grading' },
  { id: 'flat-design', label: 'Flat Design', desc: 'Vector illustration style' }
];

const VARIATION_STYLES = [
  { id: 'image-led', label: 'Image-Led', desc: 'Photo dominates, minimal text overlay' },
  { id: 'balanced', label: 'Balanced', desc: 'Equal photo and text weight' },
  { id: 'text-led', label: 'Text-Led', desc: 'Bold copy with photo as background' }
];

// Layout templates for Design Studio
const LAYOUT_TEMPLATES = {
  cleanOverlay: {
    name: 'Clean Overlay', icon: 'fas fa-layer-group',
    desc: 'Full image with text overlay on bottom',
    zones: {
      image: { x: 0, y: 0, w: 100, h: 100 },
      overlay: { x: 0, y: 50, w: 100, h: 50, opacity: 0.55 },
      headline: { x: 8, y: 55, w: 84, fontSize: 0.06, align: 'left' },
      subtext: { x: 8, y: 72, w: 84, fontSize: 0.032, align: 'left' },
      cta: { x: 8, y: 86, w: 30, fontSize: 0.025, bg: true, align: 'left' },
      logo: { x: 82, y: 4, w: 14, h: 14 }
    }
  },
  split: {
    name: 'Split', icon: 'fas fa-columns',
    desc: 'Image left, text right on brand colour',
    zones: {
      image: { x: 0, y: 0, w: 50, h: 100 },
      overlay: { x: 50, y: 0, w: 50, h: 100, opacity: 1.0, useBrandColour: true },
      headline: { x: 55, y: 25, w: 40, fontSize: 0.05, align: 'left' },
      subtext: { x: 55, y: 50, w: 40, fontSize: 0.028, align: 'left' },
      cta: { x: 55, y: 75, w: 25, fontSize: 0.022, bg: true, align: 'left' },
      logo: { x: 55, y: 5, w: 12, h: 12 }
    }
  },
  topBottom: {
    name: 'Top & Bottom', icon: 'fas fa-grip-lines',
    desc: 'Image top, text panel below',
    zones: {
      image: { x: 0, y: 0, w: 100, h: 55 },
      overlay: { x: 0, y: 55, w: 100, h: 45, opacity: 1.0, useBrandColour: true },
      headline: { x: 8, y: 60, w: 84, fontSize: 0.05, align: 'center' },
      subtext: { x: 10, y: 75, w: 80, fontSize: 0.028, align: 'center' },
      cta: { x: 35, y: 88, w: 30, fontSize: 0.022, bg: true, align: 'center' },
      logo: { x: 4, y: 58, w: 10, h: 10 }
    }
  },
  beforeAfter: {
    name: 'Before / After', icon: 'fas fa-arrows-left-right',
    desc: 'Side-by-side comparison with divider',
    zones: {
      image: { x: 0, y: 10, w: 48, h: 75 },
      image2: { x: 52, y: 10, w: 48, h: 75 },
      divider: { x: 48, y: 5, w: 4, h: 85 },
      overlay: { x: 0, y: 0, w: 100, h: 10, opacity: 0.8 },
      headline: { x: 5, y: 2, w: 90, fontSize: 0.035, align: 'center' },
      subtext: { x: 10, y: 88, w: 80, fontSize: 0.025, align: 'center' },
      cta: { x: 35, y: 93, w: 30, fontSize: 0.022, bg: true, align: 'center' },
      logo: { x: 88, y: 1, w: 8, h: 8 }
    }
  },
  testimonial: {
    name: 'Testimonial', icon: 'fas fa-quote-left',
    desc: 'Quote card with small image',
    zones: {
      image: { x: 35, y: 8, w: 30, h: 30, rounded: true },
      overlay: { x: 0, y: 0, w: 100, h: 100, opacity: 1.0, useBrandColour: true },
      headline: { x: 10, y: 45, w: 80, fontSize: 0.04, align: 'center', italic: true },
      subtext: { x: 15, y: 70, w: 70, fontSize: 0.028, align: 'center' },
      cta: { x: 35, y: 85, w: 30, fontSize: 0.022, bg: true, align: 'center' },
      logo: { x: 4, y: 4, w: 10, h: 10 }
    }
  },
  productSpotlight: {
    name: 'Spotlight', icon: 'fas fa-star',
    desc: 'Centred product with surrounding text',
    zones: {
      image: { x: 25, y: 15, w: 50, h: 50 },
      overlay: { x: 0, y: 0, w: 100, h: 100, opacity: 0.85, useBrandColour: true },
      headline: { x: 10, y: 3, w: 80, fontSize: 0.04, align: 'center' },
      subtext: { x: 15, y: 70, w: 70, fontSize: 0.028, align: 'center' },
      cta: { x: 30, y: 85, w: 40, fontSize: 0.025, bg: true, align: 'center' },
      logo: { x: 4, y: 4, w: 10, h: 10 }
    }
  }
};

const TEXT_PRESETS = {
  bold: { name: 'Bold Impact', font: 'Montserrat', headlineWeight: '900', subtextWeight: '500', headlineColour: '#ffffff', subtextColour: '#ffffffcc', ctaColour: '#ffffff', ctaBg: true },
  minimal: { name: 'Minimal Clean', font: 'Lato', headlineWeight: '300', subtextWeight: '300', headlineColour: '#ffffff', subtextColour: '#ffffffaa', ctaColour: '#ffffff', ctaBg: false },
  magazine: { name: 'Magazine', font: 'Raleway', headlineWeight: '700', subtextWeight: '400', headlineColour: '#ffffff', subtextColour: '#ffffffdd', ctaColour: '#ffffff', ctaBg: true },
  billboard: { name: 'Billboard', font: 'Bebas Neue', headlineWeight: '400', subtextWeight: '400', headlineColour: '#ffffff', subtextColour: '#ffffff', ctaColour: '#000000', ctaBg: true },
  elegant: { name: 'Elegant Serif', font: 'Playfair Display', headlineWeight: '700', subtextWeight: '400', headlineColour: '#ffffff', subtextColour: '#ffffffcc', ctaColour: '#ffffff', ctaBg: false },
  modern: { name: 'Modern Sans', font: 'Poppins', headlineWeight: '800', subtextWeight: '400', headlineColour: '#ffffff', subtextColour: '#ffffffbb', ctaColour: '#ffffff', ctaBg: true },
  handwritten: { name: 'Handwritten', font: 'Dancing Script', headlineWeight: '700', subtextWeight: '500', headlineColour: '#ffffff', subtextColour: '#ffffffcc', ctaColour: '#ffffff', ctaBg: false },
  retro: { name: 'Retro Bold', font: 'Oswald', headlineWeight: '700', subtextWeight: '400', headlineColour: '#ffffff', subtextColour: '#fffc', ctaColour: '#ffffff', ctaBg: true }
};

const BRIEFING_PHASES = [
  { text: 'Analysing your content strategy', icon: 'fas fa-chart-network' },
  { text: 'Understanding your platform', icon: 'fas fa-mobile-screen' },
  { text: 'Crafting your personalised brief', icon: 'fas fa-pen-nib' },
  { text: 'Finalising recommendations', icon: 'fas fa-sparkles' }
];

const IMAGE_GEN_STYLES = [
  { id: 'photorealistic', label: 'Photorealistic', icon: 'fas fa-camera', desc: 'Ultra-realistic DSLR quality' },
  { id: 'editorial', label: 'Editorial', icon: 'fas fa-newspaper', desc: 'Magazine-style photography' },
  { id: 'lifestyle', label: 'Lifestyle', icon: 'fas fa-sun', desc: 'Natural, candid & warm' },
  { id: 'minimal', label: 'Minimal', icon: 'fas fa-square', desc: 'Clean and simple' },
  { id: 'cinematic', label: 'Cinematic', icon: 'fas fa-film', desc: 'Dramatic, film-like' },
  { id: 'flat-design', label: 'Flat Design', icon: 'fas fa-shapes', desc: 'Vector illustration' }
];

export default function SocialPostEditor({ day, businessData, strategyData, onSave, onClose, onAutoSave }) {
  const getFullImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) return url;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${API_URL}${url}`;
  };

  // Core state
  const [activeSection, setActiveSection] = useState('briefing'); // briefing | content | images | editor | preview
  const [imageMode, setImageMode] = useState(null); // null | 'ai' | 'design'
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [generationProgress, setGenerationProgress] = useState('');

  // Content state
  const [editedContent, setEditedContent] = useState({
    postTitle: day.postTitle || day.task || '',
    generatedContent: day.generatedContent || '',
    caption: day.caption || '',
    hashtags: day.hashtags || [],
    suggestedHashtags: [],
    tone: day.tone || 'empathetic',
    format: day.format || 'standard',
    imageUrl: day.imageUrl || '',
    status: day.status || 'draft'
  });

  // Image generation state
  const [imageVariations, setImageVariations] = useState([]);
  const [selectedVariation, setSelectedVariation] = useState(0);
  const [imageStyle, setImageStyle] = useState('photorealistic');
  const [colourHarmony, setColourHarmony] = useState('complementary');
  const [imagePrompt, setImagePrompt] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(getFullImageUrl(day.imageUrl));

  // Editor state
  const [editorTab, setEditorTab] = useState('adjust');
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [canvasLoaded, setCanvasLoaded] = useState(false);
  const [brightness, setBrightness] = useState(100);
  const [contrast, setContrast] = useState(100);
  const [saturation, setSaturation] = useState(100);
  const [warmth, setWarmth] = useState(0);
  const [sharpness, setSharpness] = useState(0);
  const [vignette, setVignette] = useState(0);
  const [activeFilter, setActiveFilter] = useState('none');
  const [textLayers, setTextLayers] = useState([]);
  const [newTextInput, setNewTextInput] = useState('');
  const [textColour, setTextColour] = useState('#ffffff');
  const [textSize, setTextSize] = useState(36);
  const [textFont, setTextFont] = useState('Arial');
  const [textBold, setTextBold] = useState(true);
  const [textAlign, setTextAlign] = useState('center');
  const [textShadow, setTextShadow] = useState(true);
  const [selectedTextLayer, setSelectedTextLayer] = useState(null);

  // Shape layers
  const [shapeLayers, setShapeLayers] = useState([]);

  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [cropStart, setCropStart] = useState(null);
  const [cropEnd, setCropEnd] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  // Caption regeneration
  const [isRegeneratingCaption, setIsRegeneratingCaption] = useState(false);

  // Social publishing state
  // Initialise from day data so the Published status survives page refreshes / section switches
  const alreadyPublished = !!(day.publishedAt || day.publishedPlatform);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [publishState, setPublishState] = useState(alreadyPublished ? 'success' : null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [publishResult, setPublishResult] = useState(
    alreadyPublished
      ? { platform: day.publishedPlatform || day.platform, postedAt: day.publishedAt, postId: day.platformPostId }
      : null
  );
  const [showPublishPanel, setShowPublishPanel] = useState(false);
  const [confirmPlatform, setConfirmPlatform] = useState(null); // platform awaiting confirmation

  // Auto-save state
  const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
  const autoSaveTimerRef = useRef(null);
  // When true: after "Save Design" completes, auto-return to Preview and open publish panel
  const [pendingPublishAfterDesign, setPendingPublishAfterDesign] = useState(false);

  // Briefing state — initialise from stored brief if available (DB-persisted)
  const [briefingData, setBriefingData] = useState(day.brief || null);
  const [isBriefingLoading, setIsBriefingLoading] = useState(false);

  // Design Studio state
  const [designBaseImage, setDesignBaseImage] = useState(null);
  const [designBasePreview, setDesignBasePreview] = useState('');
  const [isGeneratingBase, setIsGeneratingBase] = useState(false);
  const [baseImageStyle, setBaseImageStyle] = useState('photorealistic');
  const [beforeAfterMode, setBeforeAfterMode] = useState(false);
  const [beforeImage, setBeforeImage] = useState('');
  const [afterImage, setAfterImage] = useState('');
  const [selectedLayout, setSelectedLayout] = useState('cleanOverlay');
  const [generatedText, setGeneratedText] = useState({ headline: '', subtext: '', ctaText: '' });
  const [isGeneratingText, setIsGeneratingText] = useState(false);
  const [textPreset, setTextPreset] = useState('bold');
  const [designLogoUrl, setDesignLogoUrl] = useState('');
  const designCanvasRef = useRef(null);

  // CTA elements state
  const [ctaUrl, setCtaUrl] = useState('');
  const [ctaShowQR, setCtaShowQR] = useState(false);
  const [ctaCustomImage, setCtaCustomImage] = useState('');

  // Base image sub-tab: 'upload' | 'generate' | 'before-after'
  const [baseImageSubTab, setBaseImageSubTab] = useState('upload');

  // Briefing loading phase animation
  const [loadingPhase, setLoadingPhase] = useState(0);

  // Carousel: per-slide images { [slideIndex]: imageUrl }
  const [slideImages, setSlideImages] = useState({});
  const [generatingSlideIdx, setGeneratingSlideIdx] = useState(null);

  const platform = PLATFORM_CONFIG[day.platform] || PLATFORM_CONFIG.instagram;
  const contentType = CONTENT_TYPES[day.contentType] || CONTENT_TYPES.educational;

  // Brand DNA extraction
  const brandDna = businessData?.brandDna || {};
  const brandColours = {
    primary: brandDna.primaryColour || '#3B82F6',
    secondary: brandDna.secondaryColour || '#1E40AF',
    accent: brandDna.accentColour || '#F59E0B'
  };

  // Parse carousel slides from generatedContent (splits on "Slide N:" markers)
  const parseCarouselSlides = (content) => {
    if (!content) return [];
    const parts = content.split(/(?=Slide\s+\d+[:\s])/i).filter(p => p.trim());
    if (parts.length <= 1) {
      // No slide markers found — split by double-newlines as fallback
      const chunks = content.split(/\n\n+/).filter(p => p.trim());
      return chunks.map((text, i) => ({ index: i, label: `Slide ${i + 1}`, text: text.trim() }));
    }
    return parts.map((text, i) => ({ index: i, label: `Slide ${i + 1}`, text: text.trim() }));
  };

  // Generate a background image for a single carousel slide
  const handleGenerateSlideImage = async (slideIdx, slideText) => {
    setGeneratingSlideIdx(slideIdx);
    try {
      const result = await businessAPI.generateImageVariations({
        prompt: `${editedContent.postTitle} — ${slideText.replace(/Slide\s+\d+[:\s]/i, '').slice(0, 150)}`,
        platform: day.platform,
        contentType: day.contentType,
        style: 'photorealistic',
        postTitle: editedContent.postTitle,
        brandColours,
        brandDna,
        count: 1
      });
      if (result?.success && result?.variations?.length) {
        const url = result.variations[0]?.url || result.variations[0];
        setSlideImages(prev => ({ ...prev, [slideIdx]: url }));
        // Also save slide images into editedContent for persistence
        setEditedContent(prev => ({
          ...prev,
          carouselImages: { ...(prev.carouselImages || {}), [slideIdx]: url }
        }));
      }
    } catch (e) {
      console.error('Slide image generation error:', e);
    } finally {
      setGeneratingSlideIdx(null);
    }
  };

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, []);

  // Auto-save: debounced save to DB whenever content changes (no editor close)
  useEffect(() => {
    if (!onAutoSave) return;
    if (!editedContent.postTitle && !editedContent.generatedContent) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    setAutoSaveStatus('saving');
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const safeImageUrl = imagePreviewUrl?.startsWith('data:') ? '' : (imagePreviewUrl || '');
        await onAutoSave(day.day, {
          ...editedContent,
          imageUrl: safeImageUrl,
          brief: briefingData,
          lastEdited: new Date().toISOString()
        });
        setAutoSaveStatus('saved');
        setTimeout(() => setAutoSaveStatus(null), 3000);
      } catch (e) {
        setAutoSaveStatus(null);
      }
    }, 3000);
    return () => { if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current); };
  }, [editedContent.generatedContent, editedContent.postTitle, editedContent.caption, editedContent.hashtags]); // eslint-disable-line

  // Load connected social accounts (for publish button)
  const [accountsLoading, setAccountsLoading] = useState(false);

  const loadConnectedAccounts = () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    console.log('[Publish] loadConnectedAccounts — fetching from server...');
    setAccountsLoading(true);
    fetch(`${API_URL}/api/social/accounts`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(r => r.json())
      .then(data => {
        console.log('[Publish] /api/social/accounts response:', data);
        if (data.success) {
          console.log('[Publish] Accounts loaded:', (data.accounts || []).map(a => `${a.platform}(${a.accountType}, isActive=${a.isActive})`));
          setConnectedAccounts(data.accounts || []);
        } else {
          console.warn('[Publish] Social accounts fetch: success=false', data);
        }
      })
      .catch(err => console.warn('[Publish] Social accounts fetch error:', err.message))
      .finally(() => setAccountsLoading(false));
  };

  useEffect(() => { loadConnectedAccounts(); }, []); // eslint-disable-line

  // DB refresh safety net: if editor opens with no generatedContent but the day was
  // previously saved (has lastEdited or brief), fetch full data from DB and hydrate state.
  useEffect(() => {
    const needsRefresh = !day.generatedContent && (day.lastEdited || day.brief);
    if (!needsRefresh) return;
    businessAPI.getRoadmap()
      .then(result => {
        if (!result.success || !result.exists) return;
        const dbDay = (result.roadmap?.calendar?.days || []).find(d => d.day === day.day || d.dayNumber === day.day);
        if (!dbDay) return;
        setEditedContent(prev => ({
          ...prev,
          generatedContent: dbDay.generatedContent || prev.generatedContent,
          caption: dbDay.caption || prev.caption,
          hashtags: dbDay.hashtags?.length ? dbDay.hashtags : prev.hashtags,
          postTitle: dbDay.postTitle || prev.postTitle,
        }));
        if (dbDay.brief && !briefingData) setBriefingData(dbDay.brief);
        if (dbDay.imageUrl && !imagePreviewUrl) setImagePreviewUrl(getFullImageUrl(dbDay.imageUrl));
      })
      .catch(() => {}); // non-blocking — editor still works without it
  }, []); // eslint-disable-line

  // Reset imageMode whenever user navigates away from the editor section
  useEffect(() => {
    if (activeSection !== 'editor') setImageMode(null);
  }, [activeSection]);

  // Filter presets
  const FILTERS = [
    { id: 'none', label: 'Original', css: 'none' },
    { id: 'warm', label: 'Warm', css: 'sepia(25%) saturate(130%) brightness(105%)' },
    { id: 'cool', label: 'Cool', css: 'hue-rotate(15deg) saturate(110%) brightness(102%)' },
    { id: 'vivid', label: 'Vivid', css: 'saturate(160%) contrast(110%)' },
    { id: 'muted', label: 'Muted', css: 'saturate(60%) brightness(105%)' },
    { id: 'bw', label: 'B&W', css: 'grayscale(100%)' },
    { id: 'vintage', label: 'Vintage', css: 'sepia(35%) contrast(90%) brightness(110%)' },
    { id: 'dramatic', label: 'Dramatic', css: 'contrast(130%) brightness(95%) saturate(120%)' },
    { id: 'fade', label: 'Fade', css: 'brightness(110%) contrast(85%) saturate(75%)' },
    { id: 'film', label: 'Film', css: 'sepia(15%) contrast(105%) saturate(90%)' }
  ];

  const FONT_OPTIONS = [
    'Montserrat', 'Poppins', 'Raleway', 'Lato', 'Open Sans', 'Roboto', 'Inter',
    'Playfair Display', 'Georgia', 'Oswald', 'Bebas Neue', 'Dancing Script',
    'Arial', 'Helvetica', 'Times New Roman', 'Impact'
  ];

  // ─── CONTENT GENERATION ───
  const handleGenerateContent = async () => {
    if (!editedContent.postTitle) return;
    setIsGenerating(true);
    try {
      const result = await businessAPI.generateSocialPost({
        dayData: {
          dayNumber: day.dayNumber || day.day,
          postTitle: editedContent.postTitle,
          contentType: day.contentType,
          platform: day.platform,
          pillar: day.pillar,
          week: day.week,
          month: day.month,
          phase: day.phase
        },
        businessData,
        strategyData,
        tone: editedContent.tone,
        format: editedContent.format
      });
      if (result.success) {
        setEditedContent(prev => ({
          ...prev,
          generatedContent: stripMarkdown(result.content),
          caption: result.caption ? stripMarkdown(result.caption) : prev.caption,
          hashtags: result.hashtags || prev.hashtags,
          suggestedHashtags: result.suggestedHashtags || []
        }));
      }
    } catch (error) {
      console.error('Content generation error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // ─── CAPTION REGENERATION ───
  const handleRegenerateCaption = async () => {
    if (!editedContent.generatedContent) return;
    setIsRegeneratingCaption(true);
    try {
      const result = await businessAPI.generateSocialPost({
        dayData: {
          dayNumber: day.dayNumber || day.day,
          postTitle: editedContent.postTitle,
          contentType: day.contentType,
          platform: day.platform,
          pillar: day.pillar
        },
        businessData,
        strategyData,
        tone: editedContent.tone,
        format: editedContent.format
      });
      if (result.success) {
        setEditedContent(prev => ({
          ...prev,
          caption: result.caption || prev.caption,
          hashtags: result.hashtags || prev.hashtags,
          suggestedHashtags: result.suggestedHashtags || []
        }));
      }
    } catch (error) {
      console.error('Caption regeneration error:', error);
    } finally {
      setIsRegeneratingCaption(false);
    }
  };

  // ─── BRIEFING GENERATION ───
  const handleLoadBriefing = async () => {
    if (briefingData) return; // Already cached
    setIsBriefingLoading(true);
    try {
      const result = await businessAPI.generateTaskBriefing({
        dayData: {
          day: day.day || day.dayNumber,
          postTitle: day.postTitle || day.task || '',
          contentType: day.contentType,
          platform: day.platform,
          pillar: day.pillar,
          format: day.format,
          tone: day.tone
        },
        businessData,
        strategyData
      });
      if (result.success && result.briefing) {
        setBriefingData(result.briefing);
        // Persist brief into editedContent so it's included when the post is saved
        setEditedContent(prev => ({ ...prev, brief: result.briefing }));
      }
    } catch (error) {
      console.error('Briefing generation error:', error);
      // Set fallback briefing
      setBriefingData({
        taskOverview: `Today you are creating a ${day.contentType || 'educational'} ${day.format || 'post'} about "${day.postTitle || 'your topic'}" for ${(PLATFORM_CONFIG[day.platform] || PLATFORM_CONFIG.instagram).name}.`,
        strategyRationale: `This content sits under your "${day.pillar || 'Brand Story'}" pillar, designed to build audience engagement on Day ${day.day || 1} of your 30-day calendar.`,
        platformTips: [
          'Use a strong visual hook to stop the scroll',
          'Keep your caption concise and value-driven',
          'End with a clear call-to-action'
        ],
        visualDirection: {
          recommended: day.contentType === 'promotional' ? 'design-studio' : 'ai-studio',
          reasoning: 'Based on your content type and platform',
          styleHint: 'Professional, brand-aligned imagery'
        },
        successMetrics: {
          primaryMetric: 'Engagement rate',
          benchmark: 'Above 3% for this content type',
          hookTip: 'Lead with your strongest visual or question'
        }
      });
    } finally {
      setIsBriefingLoading(false);
    }
  };

  // Auto-load briefing on mount
  useEffect(() => {
    if (activeSection === 'briefing' && !briefingData && !isBriefingLoading) {
      handleLoadBriefing();
    }
  }, [activeSection]);

  // Briefing loading phase animation — cycles every 1.8s while loading
  useEffect(() => {
    if (!isBriefingLoading) return;
    const interval = setInterval(() => {
      setLoadingPhase(prev => (prev + 1) % BRIEFING_PHASES.length);
    }, 1800);
    return () => clearInterval(interval);
  }, [isBriefingLoading]);

  // Auto-load logo from Brand DNA on mount
  useEffect(() => {
    const logoUrl = brandDna.logoUrl;
    if (logoUrl && !designLogoUrl) {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const fullLogoUrl = logoUrl.startsWith('http') ? logoUrl : `${API_URL}${logoUrl}`;
      setDesignLogoUrl(fullLogoUrl);
    }
    // Pre-fill CTA URL from business website if available
    if (businessData?.website && !ctaUrl) {
      setCtaUrl(businessData.website);
    }
  }, []);

  // ─── IMAGE TEXT GENERATION (for Design Studio) ───
  const handleGenerateImageText = async () => {
    setIsGeneratingText(true);
    try {
      const result = await businessAPI.generateImageText({
        postTitle: editedContent.postTitle,
        postContent: editedContent.generatedContent,
        businessName: businessData?.businessName,
        industry: businessData?.industry || businessData?.businessType,
        platform: day.platform,
        contentType: day.contentType,
        tone: editedContent.tone
      });
      if (result.success) {
        setGeneratedText({
          headline: result.headline || '',
          subtext: result.subtext || '',
          ctaText: result.ctaText || ''
        });
      }
    } catch (error) {
      console.error('Image text generation error:', error);
    } finally {
      setIsGeneratingText(false);
    }
  };

  // ─── DESIGN STUDIO: Generate Base Image ───
  const handleGenerateBaseImage = async () => {
    setIsGeneratingBase(true);
    try {
      const result = await businessAPI.generateBaseImage({
        dayData: {
          postTitle: editedContent.postTitle || day.postTitle || day.task || '',
          contentType: day.contentType,
          platform: day.platform
        },
        platform: day.platform,
        style: baseImageStyle
      });

      if (result.success && result.imageUrl) {
        const fullUrl = getFullImageUrl(result.imageUrl);
        setDesignBasePreview(fullUrl);
      }
    } catch (error) {
      console.error('Base image generation error:', error);
      alert('Failed to generate base image. Please try again or upload one.');
    } finally {
      setIsGeneratingBase(false);
    }
  };

  // ─── DESIGN STUDIO: Upload handlers ───
  const handleDesignBaseUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setDesignBasePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBeforeImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setBeforeImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleAfterImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setAfterImage(reader.result);
    reader.readAsDataURL(file);
  };

  // ─── DESIGN STUDIO: Canvas Renderer ───
  useEffect(() => {
    if (activeSection !== 'images' || !designCanvasRef.current) return;
    const canvas = designCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const size = 1080;
    canvas.width = size;
    canvas.height = size;

    const layout = LAYOUT_TEMPLATES[selectedLayout];
    if (!layout) return;

    const preset = TEXT_PRESETS[textPreset] || TEXT_PRESETS.bold;
    const zones = layout.zones;

    // Clear canvas
    ctx.fillStyle = '#1e293b';
    ctx.fillRect(0, 0, size, size);

    const drawComposition = () => {
      // Draw overlay/background
      if (zones.overlay) {
        if (zones.overlay.useBrandColour) {
          ctx.fillStyle = brandColours.primary;
        } else {
          ctx.fillStyle = `rgba(0,0,0,${zones.overlay.opacity})`;
        }
        ctx.fillRect(
          size * zones.overlay.x / 100, size * zones.overlay.y / 100,
          size * zones.overlay.w / 100, size * zones.overlay.h / 100
        );
      }

      // Draw divider for before/after
      if (zones.divider) {
        ctx.fillStyle = brandColours.accent;
        ctx.fillRect(
          size * zones.divider.x / 100, size * zones.divider.y / 100,
          size * zones.divider.w / 100, size * zones.divider.h / 100
        );
      }

      // Draw text
      if (zones.headline && generatedText.headline) {
        const fSize = Math.round(size * zones.headline.fontSize);
        ctx.font = `${preset.headlineWeight} ${zones.headline.italic ? 'italic ' : ''}${fSize}px ${preset.font}`;
        ctx.fillStyle = preset.headlineColour;
        ctx.textAlign = zones.headline.align || 'left';
        const tx = zones.headline.align === 'center' ? size * (zones.headline.x + zones.headline.w / 2) / 100 : size * zones.headline.x / 100;
        const maxW = size * zones.headline.w / 100;

        // Word wrap headline
        const words = generatedText.headline.split(' ');
        let line = '';
        let yPos = size * zones.headline.y / 100;
        words.forEach(word => {
          const testLine = line + (line ? ' ' : '') + word;
          if (ctx.measureText(testLine).width > maxW && line) {
            ctx.fillText(line, tx, yPos);
            line = word;
            yPos += fSize * 1.2;
          } else {
            line = testLine;
          }
        });
        ctx.fillText(line, tx, yPos);
      }

      if (zones.subtext && generatedText.subtext) {
        const fSize = Math.round(size * zones.subtext.fontSize);
        ctx.font = `${preset.subtextWeight} ${fSize}px ${preset.font}`;
        ctx.fillStyle = preset.subtextColour;
        ctx.textAlign = zones.subtext.align || 'left';
        const tx = zones.subtext.align === 'center' ? size * (zones.subtext.x + zones.subtext.w / 2) / 100 : size * zones.subtext.x / 100;
        const maxW = size * zones.subtext.w / 100;
        const words = generatedText.subtext.split(' ');
        let line = '';
        let yPos = size * zones.subtext.y / 100;
        words.forEach(word => {
          const testLine = line + (line ? ' ' : '') + word;
          if (ctx.measureText(testLine).width > maxW && line) {
            ctx.fillText(line, tx, yPos);
            line = word;
            yPos += fSize * 1.4;
          } else {
            line = testLine;
          }
        });
        ctx.fillText(line, tx, yPos);
      }

      if (zones.cta && generatedText.ctaText) {
        const fSize = Math.round(size * zones.cta.fontSize);
        ctx.font = `700 ${fSize}px ${preset.font}`;
        const tx = zones.cta.align === 'center' ? size * (zones.cta.x + zones.cta.w / 2) / 100 : size * zones.cta.x / 100;
        const ty = size * zones.cta.y / 100;

        if (preset.ctaBg || zones.cta.bg) {
          const metrics = ctx.measureText(generatedText.ctaText);
          const pad = fSize * 0.5;
          const bx = zones.cta.align === 'center' ? tx - metrics.width / 2 - pad : tx - pad;
          ctx.fillStyle = brandColours.accent;
          ctx.beginPath();
          const rw = metrics.width + pad * 2;
          const rh = fSize + pad;
          ctx.roundRect(bx, ty - fSize * 0.2, rw, rh, fSize * 0.2);
          ctx.fill();
        }
        ctx.fillStyle = preset.ctaColour;
        ctx.textAlign = zones.cta.align || 'left';
        ctx.fillText(generatedText.ctaText, tx, ty + fSize * 0.6);
      }

      // Draw QR code if enabled
      if (ctaShowQR && ctaUrl) {
        // Simple QR placeholder — draw a branded QR-style block in corner
        const qrSize = size * 0.12;
        const qrX = size * 0.05;
        const qrY = size * 0.82;
        const cellSize = qrSize / 7;

        // Outer border
        ctx.fillStyle = brandColours.primary;
        ctx.fillRect(qrX - 4, qrY - 4, qrSize + 8, qrSize + 8);
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(qrX, qrY, qrSize, qrSize);

        // QR-style data cells (simplified visual representation)
        ctx.fillStyle = brandColours.primary;
        const pattern = [
          [1,1,1,0,1,1,1], [1,0,1,0,1,0,1], [1,1,1,0,1,1,1],
          [0,0,0,0,0,0,0], [1,1,1,0,1,0,1], [0,1,0,0,0,1,0], [1,1,1,0,1,1,1]
        ];
        pattern.forEach((row, ri) => row.forEach((cell, ci) => {
          if (cell) ctx.fillRect(qrX + ci * cellSize, qrY + ri * cellSize, cellSize - 0.5, cellSize - 0.5);
        }));

        // URL below QR
        if (ctaUrl) {
          ctx.fillStyle = 'rgba(0,0,0,0.6)';
          ctx.fillRect(qrX - 4, qrY + qrSize + 4, qrSize + 8, 22);
          ctx.font = `500 ${Math.round(size * 0.014)}px Poppins, Arial`;
          ctx.fillStyle = '#ffffff';
          ctx.textAlign = 'center';
          const shortUrl = ctaUrl.replace(/^https?:\/\/(www\.)?/, '').substring(0, 20);
          ctx.fillText(shortUrl, qrX + qrSize / 2, qrY + qrSize + 18);
        }
      }

      // Draw logo (auto-loaded from Brand DNA or manually uploaded)
      if (designLogoUrl && zones.logo) {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.onload = () => {
          const lx = size * zones.logo.x / 100;
          const ly = size * zones.logo.y / 100;
          const lw = size * zones.logo.w / 100;
          const lh = size * zones.logo.h / 100;
          // Draw semi-transparent background behind logo for legibility
          ctx.globalAlpha = 0.85;
          ctx.fillStyle = 'rgba(255,255,255,0.15)';
          ctx.beginPath();
          ctx.roundRect(lx - 4, ly - 4, lw + 8, lh + 8, 6);
          ctx.fill();
          ctx.globalAlpha = 1;
          // Fit logo within zone maintaining aspect ratio
          const logoRatio = logoImg.width / logoImg.height;
          let dlw = lw, dlh = lh;
          if (logoRatio > 1) dlh = lw / logoRatio;
          else dlw = lh * logoRatio;
          ctx.drawImage(logoImg, lx + (lw - dlw) / 2, ly + (lh - dlh) / 2, dlw, dlh);
        };
        logoImg.onerror = () => {};
        logoImg.src = designLogoUrl;
      }

      // Reset alignment
      ctx.textAlign = 'left';
    };

    // Load and draw base image(s)
    const baseUrl = beforeAfterMode ? beforeImage : designBasePreview;
    if (baseUrl) {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        // Clear and redraw
        ctx.fillStyle = '#1e293b';
        ctx.fillRect(0, 0, size, size);

        const imgZone = zones.image;
        const dx = size * imgZone.x / 100;
        const dy = size * imgZone.y / 100;
        const dw = size * imgZone.w / 100;
        const dh = size * imgZone.h / 100;

        if (imgZone.rounded) {
          ctx.save();
          ctx.beginPath();
          ctx.arc(dx + dw / 2, dy + dh / 2, Math.min(dw, dh) / 2, 0, Math.PI * 2);
          ctx.clip();
        }

        // Cover-fit the image into the zone
        const imgRatio = img.width / img.height;
        const zoneRatio = dw / dh;
        let sx = 0, sy = 0, sw = img.width, sh = img.height;
        if (imgRatio > zoneRatio) {
          sw = img.height * zoneRatio;
          sx = (img.width - sw) / 2;
        } else {
          sh = img.width / zoneRatio;
          sy = (img.height - sh) / 2;
        }
        ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);

        if (imgZone.rounded) ctx.restore();

        // Draw second image for before/after
        if (beforeAfterMode && afterImage && zones.image2) {
          const img2 = new Image();
          img2.crossOrigin = 'anonymous';
          img2.onload = () => {
            const z2 = zones.image2;
            const d2x = size * z2.x / 100;
            const d2y = size * z2.y / 100;
            const d2w = size * z2.w / 100;
            const d2h = size * z2.h / 100;
            const ir2 = img2.width / img2.height;
            const zr2 = d2w / d2h;
            let s2x = 0, s2y = 0, s2w = img2.width, s2h = img2.height;
            if (ir2 > zr2) { s2w = img2.height * zr2; s2x = (img2.width - s2w) / 2; }
            else { s2h = img2.width / zr2; s2y = (img2.height - s2h) / 2; }
            ctx.drawImage(img2, s2x, s2y, s2w, s2h, d2x, d2y, d2w, d2h);
            drawComposition();
          };
          img2.src = afterImage;
        } else {
          drawComposition();
        }
      };
      img.onerror = () => drawComposition();
      img.src = baseUrl;
    } else {
      // No image, just draw layout with placeholder
      if (zones.overlay) {
        ctx.fillStyle = zones.overlay.useBrandColour ? brandColours.primary : '#334155';
        ctx.fillRect(0, 0, size, size);
      }
      // Placeholder icon
      ctx.fillStyle = '#475569';
      ctx.font = '48px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('Upload an image to begin', size / 2, size * 0.4);
      ctx.textAlign = 'left';
      drawComposition();
    }
  }, [selectedLayout, textPreset, generatedText, designBasePreview, beforeAfterMode, beforeImage, afterImage, brandColours, designLogoUrl, ctaUrl, ctaShowQR, ctaCustomImage, activeSection]);

  // ─── DESIGN STUDIO: Apply to Editor ───
  const applyDesignToEditor = () => {
    if (!designCanvasRef.current) return;
    const dataUrl = designCanvasRef.current.toDataURL('image/png');
    setImagePreviewUrl(dataUrl);
    setEditedContent(prev => ({ ...prev, imageUrl: dataUrl }));
    setActiveSection('editor');
  };

  // Toggle hashtag
  const toggleHashtag = (tag) => {
    setEditedContent(prev => {
      const exists = prev.hashtags.includes(tag);
      return { ...prev, hashtags: exists ? prev.hashtags.filter(h => h !== tag) : [...prev.hashtags, tag] };
    });
  };

  // ─── IMAGE GENERATION (3 VARIATIONS) ───
  const handleGenerateImages = async () => {
    setIsGeneratingImages(true);
    setGenerationProgress('Analysing brand identity...');
    setImageVariations([]);

    try {
      // Build prompt context from brand DNA + day data
      const prompt = imagePrompt || editedContent.postTitle;

      setGenerationProgress('Generating 3 brand-aligned variations...');

      const result = await businessAPI.generateImageVariations({
        prompt,
        platform: day.platform,
        contentType: day.contentType,
        style: imageStyle,
        colourHarmony,
        businessName: businessData?.businessName,
        industry: businessData?.industry || businessData?.businessType,
        brandColours,
        brandDna: {
          visualTone: brandDna.visualTone,
          contentPreference: brandDna.contentPreference,
          brandFeeling: brandDna.brandFeeling
        },
        postTitle: editedContent.postTitle,
        postContent: editedContent.generatedContent
      });

      if (result.success && result.variations) {
        setImageVariations(result.variations.map(v => ({
          ...v,
          url: getFullImageUrl(v.url)
        })));
        setSelectedVariation(0);
        // Set first variation as preview
        if (result.variations[0]) {
          const url = getFullImageUrl(result.variations[0].url);
          setImagePreviewUrl(url);
          setEditedContent(prev => ({ ...prev, imageUrl: result.variations[0].url }));
        }
        setGenerationProgress('');
      } else {
        setGenerationProgress('');
        alert('Failed to generate images. Please try again.');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      setGenerationProgress('');
      alert('An error occurred generating images.');
    } finally {
      setIsGeneratingImages(false);
    }
  };

  // Select a variation
  const handleSelectVariation = (index) => {
    setSelectedVariation(index);
    const variation = imageVariations[index];
    if (variation) {
      setImagePreviewUrl(variation.url);
      setEditedContent(prev => ({ ...prev, imageUrl: variation.url }));
    }
  };

  // ─── IMAGE UPLOAD ───
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('File size must be less than 10MB');
        return;
      }
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result);
        setEditedContent(prev => ({ ...prev, imageUrl: reader.result }));
        setImageVariations([]);
      };
      reader.readAsDataURL(file);
    }
  };

  // ─── CANVAS EDITOR ───
  // Load image into canvas when entering editor
  useEffect(() => {
    if (activeSection !== 'editor' || !imagePreviewUrl) return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setCanvasLoaded(true);
    };
    img.onerror = () => {
      // Retry without CORS
      const fallback = new Image();
      fallback.onload = () => {
        imageRef.current = fallback;
        setCanvasLoaded(true);
      };
      fallback.src = imagePreviewUrl;
    };
    img.src = imagePreviewUrl;
  }, [activeSection, imagePreviewUrl]);

  // Render canvas
  useEffect(() => {
    if (!canvasLoaded || !canvasRef.current || !imageRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    // Apply filters
    const filterCSS = FILTERS.find(f => f.id === activeFilter)?.css || 'none';
    const adjustCSS = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    ctx.filter = filterCSS === 'none' ? adjustCSS : `${adjustCSS} ${filterCSS}`;
    ctx.drawImage(img, 0, 0);
    ctx.filter = 'none';

    // Vignette
    if (vignette > 0) {
      const gradient = ctx.createRadialGradient(
        canvas.width / 2, canvas.height / 2, canvas.width * 0.3,
        canvas.width / 2, canvas.height / 2, canvas.width * 0.7
      );
      gradient.addColorStop(0, 'rgba(0,0,0,0)');
      gradient.addColorStop(1, `rgba(0,0,0,${vignette / 100})`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Shape layers
    shapeLayers.forEach(shape => {
      ctx.globalAlpha = shape.opacity / 100;
      ctx.fillStyle = shape.colour;
      if (shape.type === 'rect') {
        ctx.fillRect(shape.x, shape.y, shape.w, shape.h);
      } else if (shape.type === 'circle') {
        ctx.beginPath();
        ctx.arc(shape.x + shape.w / 2, shape.y + shape.h / 2, Math.min(shape.w, shape.h) / 2, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    });

    // Text layers
    textLayers.forEach(layer => {
      ctx.font = `${layer.bold ? 'bold ' : ''}${layer.size}px ${layer.font}`;
      ctx.fillStyle = layer.colour;
      ctx.textAlign = layer.align;
      ctx.textBaseline = 'top';

      if (layer.shadow) {
        ctx.shadowColor = 'rgba(0,0,0,0.6)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
      }

      // Word wrap
      const maxWidth = canvas.width * 0.8;
      const words = layer.text.split(' ');
      let line = '';
      let y = layer.y;
      const lineHeight = layer.size * 1.3;
      const x = layer.align === 'center' ? canvas.width / 2 : layer.align === 'right' ? canvas.width - 40 : 40;

      words.forEach(word => {
        const testLine = line + (line ? ' ' : '') + word;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && line) {
          ctx.fillText(line, x, y);
          line = word;
          y += lineHeight;
        } else {
          line = testLine;
        }
      });
      ctx.fillText(line, x, y);

      ctx.shadowColor = 'transparent';
      ctx.textAlign = 'left';
    });

    // Crop overlay
    if (cropMode && cropStart && cropEnd) {
      ctx.fillStyle = 'rgba(0,0,0,0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      const x = Math.min(cropStart.x, cropEnd.x);
      const y = Math.min(cropStart.y, cropEnd.y);
      const w = Math.abs(cropEnd.x - cropStart.x);
      const h = Math.abs(cropEnd.y - cropStart.y);
      ctx.clearRect(x, y, w, h);
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 3]);
      ctx.strokeRect(x, y, w, h);
      ctx.setLineDash([]);
    }
  }, [canvasLoaded, brightness, contrast, saturation, warmth, sharpness, vignette, activeFilter, textLayers, shapeLayers, cropMode, cropStart, cropEnd]);

  // Canvas mouse handlers
  const getCanvasCoords = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return { x: Math.round((e.clientX - rect.left) * scaleX), y: Math.round((e.clientY - rect.top) * scaleY) };
  };

  const handleCanvasMouseDown = (e) => {
    if (cropMode) {
      setCropStart(getCanvasCoords(e));
      setCropEnd(null);
      setIsCropping(true);
    }
  };
  const handleCanvasMouseMove = (e) => {
    if (isCropping && cropMode) setCropEnd(getCanvasCoords(e));
  };
  const handleCanvasMouseUp = () => setIsCropping(false);

  const applyCrop = () => {
    if (!cropStart || !cropEnd || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const x = Math.min(cropStart.x, cropEnd.x);
    const y = Math.min(cropStart.y, cropEnd.y);
    const w = Math.abs(cropEnd.x - cropStart.x);
    const h = Math.abs(cropEnd.y - cropStart.y);
    if (w < 10 || h < 10) return;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w;
    tempCanvas.height = h;
    tempCanvas.getContext('2d').drawImage(canvas, x, y, w, h, 0, 0, w, h);

    const newImg = new Image();
    newImg.onload = () => {
      imageRef.current = newImg;
      setCropMode(false);
      setCropStart(null);
      setCropEnd(null);
      setCanvasLoaded(prev => !prev); // trigger re-render
      setTimeout(() => setCanvasLoaded(true), 50);
    };
    newImg.src = tempCanvas.toDataURL('image/png');
  };

  // Add text layer
  const addTextLayer = () => {
    if (!newTextInput.trim() || !canvasRef.current) return;
    setTextLayers(prev => [...prev, {
      text: newTextInput,
      colour: textColour,
      size: textSize,
      font: textFont,
      bold: textBold,
      align: textAlign,
      shadow: textShadow,
      x: 40,
      y: 60 + prev.length * (textSize + 20)
    }]);
    setNewTextInput('');
  };

  const updateTextLayer = (index, updates) => {
    setTextLayers(prev => prev.map((l, i) => i === index ? { ...l, ...updates } : l));
  };

  const removeTextLayer = (index) => {
    setTextLayers(prev => prev.filter((_, i) => i !== index));
    if (selectedTextLayer === index) setSelectedTextLayer(null);
  };

  // Add shape
  const addShape = (type) => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    setShapeLayers(prev => [...prev, {
      type,
      x: canvas.width * 0.1,
      y: canvas.height * 0.7,
      w: type === 'circle' ? canvas.width * 0.15 : canvas.width * 0.8,
      h: type === 'circle' ? canvas.width * 0.15 : canvas.height * 0.08,
      colour: brandColours.primary,
      opacity: 80
    }]);
  };

  // Save from editor
  const saveFromEditor = () => {
    if (!canvasRef.current) return;
    const dataUrl = canvasRef.current.toDataURL('image/png');
    setImagePreviewUrl(dataUrl);
    setEditedContent(prev => ({ ...prev, imageUrl: dataUrl }));
    setActiveSection('preview');
  };

  // Reset editor
  const resetEditor = () => {
    setBrightness(100); setContrast(100); setSaturation(100);
    setWarmth(0); setSharpness(0); setVignette(0);
    setActiveFilter('none'); setTextLayers([]); setShapeLayers([]);
    setCropMode(false); setCropStart(null); setCropEnd(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => { imageRef.current = img; setCanvasLoaded(prev => !prev); setTimeout(() => setCanvasLoaded(true), 50); };
    img.src = imagePreviewUrl;
  };

  // ─── STATUS WORKFLOW ───
  const getNextStatus = (s) => { const i = STATUS_FLOW.indexOf(s || 'draft'); return i < STATUS_FLOW.length - 1 ? STATUS_FLOW[i + 1] : s; };
  const getPrevStatus = (s) => { const i = STATUS_FLOW.indexOf(s || 'draft'); return i > 0 ? STATUS_FLOW[i - 1] : s; };

  const handleAdvanceStatus = () => {
    const next = getNextStatus(editedContent.status);
    setEditedContent(prev => ({
      ...prev, status: next, [`${next}At`]: new Date().toISOString(),
      statusHistory: [...(prev.statusHistory || []), { from: prev.status, to: next, at: new Date().toISOString() }]
    }));
  };

  const handleRevertStatus = () => {
    const prev = getPrevStatus(editedContent.status);
    setEditedContent(p => ({
      ...p, status: prev,
      statusHistory: [...(p.statusHistory || []), { from: p.status, to: prev, at: new Date().toISOString() }]
    }));
  };

  // ─── SAVE ───
  const handleSave = () => {
    if (!editedContent.postTitle) {
      alert('Please add a post title before saving.');
      return;
    }
    let saveStatus = editedContent.status || 'draft';
    if (saveStatus === 'draft' && editedContent.generatedContent) saveStatus = 'edited';

    // Strip base64 data before saving — only keep server-side paths
    const safeImageUrl = imagePreviewUrl?.startsWith('data:') ? '' : (imagePreviewUrl || '');
    const safeVariations = imageVariations.map(v => v.url).filter(u => u && !u.startsWith('data:'));
    onSave(day.day, {
      ...editedContent,
      imageUrl: safeImageUrl,
      status: saveStatus,
      brief: briefingData,
      lastEdited: new Date().toISOString(),
      statusHistory: editedContent.statusHistory || [],
      selectedVariation: selectedVariation,
      imageVariations: safeVariations,
    });
  };

  // ─── PUBLISH TO SOCIAL ───
  const handlePublish = async (targetPlatform) => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    // ✅ FIX: Always send the full generated content as the post body, not just the short caption hook
    const postBody = editedContent.generatedContent || editedContent.caption || '';
    if (!postBody) { alert('Please generate content before publishing.'); return; }

    // ✅ FIX: Avoid duplicate hashtags — if the generated content already contains #tags, don't append them again
    const contentHasHashtags = postBody.includes('#');
    const hashtagsToSend = contentHasHashtags ? [] : (editedContent.hashtags || []);

    // Resolve the best available image URL to send to Meta:
    // Priority: imagePreviewUrl (most recent) → editedContent.imageUrl (saved)
    // Strip data: and blob: URLs — Meta cannot use them.
    // Strip localhost URLs — Meta cannot reach them in development.
    // If nothing usable is found we still proceed and let Meta's API return its own error.
    const candidateUrl = (
      (imagePreviewUrl && !imagePreviewUrl.startsWith('data:') && !imagePreviewUrl.startsWith('blob:') ? imagePreviewUrl : null) ||
      (editedContent.imageUrl && !editedContent.imageUrl.startsWith('data:') && !editedContent.imageUrl.startsWith('blob:') ? editedContent.imageUrl : null) ||
      ''
    );
    const isLocalUrl = candidateUrl && (candidateUrl.includes('localhost') || candidateUrl.includes('127.0.0.1'));
    const safeImageUrl = isLocalUrl ? '' : candidateUrl;

    console.log('[Publish] candidateUrl:', candidateUrl, '| isLocalUrl:', isLocalUrl, '| safeImageUrl:', safeImageUrl);

    // ── Auto-upload data: URL images so Instagram can access them via a server URL ──
    // Design Studio exports canvas as data: URLs. Meta's API cannot use these —
    // they need a publicly accessible HTTP(S) URL. We upload to our own server first.
    let finalImageUrl = safeImageUrl;
    const dataUrlImage = (imagePreviewUrl?.startsWith('data:') ? imagePreviewUrl : null)
                      || (editedContent.imageUrl?.startsWith('data:') ? editedContent.imageUrl : null);

    if (!finalImageUrl && dataUrlImage) {
      console.log('[Publish] data: URL image detected — uploading to server to get a public URL...');
      setPublishState('loading');
      setPublishResult(null);
      try {
        const uploadRes = await fetch(`${API_URL}/api/assets/save-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            imageData: dataUrlImage,
            fileName: `post_day${day.day}_${Date.now()}.png`,
            category: 'social'
          })
        }).then(r => r.json());
        if (uploadRes.success && uploadRes.asset?.fileUrl) {
          finalImageUrl = `${API_URL}${uploadRes.asset.fileUrl}`;
          console.log('[Publish] Auto-upload OK → server URL:', finalImageUrl);
          setImagePreviewUrl(finalImageUrl); // cache so future saves use the server URL
        } else {
          console.warn('[Publish] Auto-upload failed:', uploadRes.error);
          setPublishState('error');
          setPublishResult({ error: `Image upload failed: ${uploadRes.error || 'Unknown error'}. Please try again or save the image manually first.` });
          return;
        }
      } catch (uploadErr) {
        console.warn('[Publish] Auto-upload network error:', uploadErr.message);
        setPublishState('error');
        setPublishResult({ error: 'Could not upload image to server. Check your connection and try again.' });
        return;
      }
    }

    // Instagram requires a publicly accessible HTTPS image URL.
    // localhost URLs are only reachable from this machine — Meta's servers can't download them.
    const isLocalhostUrl = finalImageUrl && (finalImageUrl.includes('localhost') || finalImageUrl.includes('127.0.0.1'));
    if (targetPlatform === 'instagram' && isLocalhostUrl) {
      setPublishState('error');
      setPublishResult({
        error: 'Instagram cannot access images from localhost. This works once deployed to production. ✅'
      });
      return;
    }

    // Instagram requires an image — block with clear message if none available
    if (targetPlatform === 'instagram' && !finalImageUrl) {
      setPublishState('error');
      setPublishResult({ error: 'Instagram requires an image. Go to the Editor tab, generate or upload an image, then publish.' });
      return;
    }

    setPublishState('loading');
    setPublishResult(null);
    try {
      const res = await fetch(`${API_URL}/api/social/post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          platform: targetPlatform,
          caption: postBody,
          imageUrl: finalImageUrl || undefined,
          hashtags: hashtagsToSend,
          dayNumber: day.day
        })
      }).then(r => r.json());

      if (res.success) {
        setPublishState('success');
        setPublishResult({ platform: targetPlatform, postId: res.platformPostId, postedAt: res.postedAt });
        setShowSuccessOverlay(true); // big green tick in the middle of the editor
        // ✅ Use onAutoSave (not onSave) so the editor stays open showing "Published!"
        // onSave would auto-close the editor after 800ms — bad UX after a publish
        const safeVariations = imageVariations.map(v => v.url).filter(u => u && !u.startsWith('data:'));
        const publishPayload = {
          ...editedContent,
          imageUrl: finalImageUrl,
          status: 'completed',
          brief: briefingData,
          publishedAt: res.postedAt,
          platformPostId: res.platformPostId,
          publishedPlatform: targetPlatform,
          completedAt: new Date().toISOString(),
          lastEdited: new Date().toISOString(),
          statusHistory: [...(editedContent.statusHistory || []), { from: editedContent.status, to: 'completed', at: new Date().toISOString() }],
          selectedVariation,
          imageVariations: safeVariations,
        };
        if (onAutoSave) {
          onAutoSave(day.day, publishPayload).catch(e => console.warn('Post-publish save failed (non-critical):', e.message));
        } else {
          onSave(day.day, publishPayload); // fallback if onAutoSave not wired yet
        }
      } else {
        setPublishState('error');
        setPublishResult({ error: res.message || 'Publishing failed. Please try again.' });
      }
    } catch (err) {
      setPublishState('error');
      setPublishResult({ error: 'Network error — check your connection and try again.' });
    }
  };

  // ─── DOWNLOADS ───
  const handleDownloadImage = async () => {
    if (!imagePreviewUrl) return;
    try {
      const response = await fetch(imagePreviewUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${editedContent.postTitle.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    }
  };

  const handleCopyText = async () => {
    if (!editedContent.generatedContent) return;
    try {
      await navigator.clipboard.writeText(editedContent.generatedContent);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = editedContent.generatedContent;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  // ─── NAV SECTIONS ───
  const sections = [
    { id: 'briefing', label: 'Briefing', icon: 'fas fa-clipboard-list' },
    { id: 'content', label: 'Content', icon: 'fas fa-pen' },
    { id: 'editor', label: 'Design Studio', icon: 'fas fa-wand-magic-sparkles' },
    { id: 'preview', label: 'Preview', icon: 'fas fa-eye' }
  ];

  return (
    <div className="flex flex-col h-full bg-white relative">

      {/* ─── TOP BAR ─── */}
      <div className="flex-shrink-0 border-b border-slate-200 bg-white">
        {/* Post Info Bar */}
        <div className="px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: platform.color }}>
              <i className={`${platform.icon} text-white text-sm`}></i>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-slate-900">{platform.name} Post</h2>
                <span className="text-slate-300">|</span>
                <span className="text-xs text-slate-500">Day {day.day}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded" style={{ backgroundColor: contentType.color + '20', color: contentType.color }}>
                  {contentType.label}
                </span>
              </div>
              <p className="text-[11px] text-slate-400 mt-0.5 truncate max-w-md">{day.pillar || 'Content'}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Section progress indicator */}
            <div className="flex items-center gap-1 mr-2">
              {sections.map((sec, idx) => (
                <div key={sec.id} className={`w-1.5 h-1.5 rounded-full transition-all ${
                  activeSection === sec.id ? 'bg-slate-800 w-4' : 'bg-slate-300'
                }`}></div>
              ))}
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>

        {/* Section Navigation */}
        <div className="px-5 flex items-center gap-0.5">
          {sections.map((sec) => (
            <button key={sec.id}
              onClick={() => !sec.disabled && setActiveSection(sec.id)}
              disabled={sec.disabled}
              className={`px-4 py-2.5 text-xs font-semibold transition-all border-b-2 ${
                activeSection === sec.id
                  ? 'text-slate-900 border-slate-900'
                  : sec.disabled
                    ? 'text-slate-300 border-transparent cursor-not-allowed'
                    : 'text-slate-400 border-transparent hover:text-slate-600'
              }`}>
              <i className={`${sec.icon} mr-1.5`}></i>{sec.label}
            </button>
          ))}
        </div>
      </div>

      {/* ─── MAIN CONTENT AREA ─── */}
      <div className="flex-1 overflow-y-auto">

        {/* ═══ BRIEFING SECTION ═══ */}
        {activeSection === 'briefing' && (
          <div className="p-5 max-w-3xl mx-auto space-y-4">
            {isBriefingLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                {/* Animated concentric rings */}
                <div className="relative w-24 h-24 mb-8">
                  <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
                  <div className="absolute inset-0 rounded-full border-4 border-t-slate-800 border-r-slate-600 border-b-transparent border-l-transparent animate-spin"></div>
                  <div className="absolute inset-3 rounded-full border-4 border-t-transparent border-r-transparent border-b-slate-400 border-l-slate-300 animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.9s' }}></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className={`${BRIEFING_PHASES[loadingPhase].icon} text-slate-700 text-lg`}></i>
                  </div>
                </div>

                {/* Phase text */}
                <div className="text-center mb-6" style={{ minHeight: '48px' }}>
                  <p className="text-base font-bold text-slate-900 transition-all duration-500">{BRIEFING_PHASES[loadingPhase].text}</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    <span className="text-slate-400 text-sm">.</span>
                    <span className="text-slate-500 text-sm animate-bounce" style={{ animationDelay: '0.1s' }}>.</span>
                    <span className="text-slate-600 text-sm animate-bounce" style={{ animationDelay: '0.2s' }}>.</span>
                  </div>
                </div>

                {/* Progress steps */}
                <div className="flex items-center gap-2 mb-4">
                  {BRIEFING_PHASES.map((phase, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-all duration-500 ${
                        i < loadingPhase ? 'bg-slate-800' : i === loadingPhase ? 'bg-slate-600 ring-2 ring-slate-300' : 'bg-slate-200'
                      }`}>
                        {i < loadingPhase ? (
                          <i className="fas fa-check text-white text-[9px]"></i>
                        ) : (
                          <span className="text-[9px] font-bold text-white">{i + 1}</span>
                        )}
                      </div>
                      {i < BRIEFING_PHASES.length - 1 && (
                        <div className={`w-8 h-0.5 transition-all duration-500 ${i < loadingPhase ? 'bg-slate-800' : 'bg-slate-200'}`}></div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div className="w-64 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-slate-800 rounded-full transition-all duration-700"
                    style={{ width: `${((loadingPhase + 1) / BRIEFING_PHASES.length) * 100}%` }}
                  ></div>
                </div>
                <p className="text-[10px] text-slate-400 mt-3">This takes about 10 seconds</p>
              </div>
            ) : briefingData ? (
              <>
                {/* Header */}
                <div className="bg-slate-900 text-white rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: platform.color }}>
                      <i className={`${platform.icon} text-white text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">Day {day.day} Brief</p>
                      <p className="text-sm font-bold">{day.postTitle || day.task || 'Today\'s Content'}</p>
                    </div>
                  </div>
                  <p className="text-xs text-slate-300 leading-relaxed mt-3">{briefingData.taskOverview}</p>
                </div>

                {/* Strategy Rationale */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center">
                      <i className="fas fa-chess text-blue-500 text-[10px]"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900">Strategy Rationale</h3>
                  </div>
                  <p className="text-[11px] text-slate-600 leading-relaxed">{briefingData.strategyRationale}</p>
                </div>

                {/* Platform Tips */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-purple-50 rounded-lg flex items-center justify-center">
                      <i className="fas fa-lightbulb text-purple-500 text-[10px]"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900">{platform.name} Tips</h3>
                  </div>
                  <div className="space-y-2">
                    {(briefingData.platformTips || []).map((tip, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="w-5 h-5 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-[9px] font-bold text-slate-500">{i + 1}</span>
                        </span>
                        <p className="text-[11px] text-slate-600 leading-relaxed">{tip}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Visual Direction */}
                <div className="bg-white border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-6 h-6 bg-amber-50 rounded-lg flex items-center justify-center">
                      <i className="fas fa-palette text-amber-500 text-[10px]"></i>
                    </div>
                    <h3 className="text-xs font-bold text-slate-900">Visual Direction</h3>
                  </div>
                  {briefingData.visualDirection && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-[10px] font-bold rounded-md ${
                          briefingData.visualDirection.recommended === 'ai-studio'
                            ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                        }`}>
                          {briefingData.visualDirection.recommended === 'ai-studio' ? 'AI Studio' : 'Design Studio'} Recommended
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-600">{briefingData.visualDirection.reasoning}</p>
                      <p className="text-[11px] text-slate-500 italic">{briefingData.visualDirection.styleHint}</p>
                    </div>
                  )}
                </div>

                {/* Success Metrics */}
                {briefingData.successMetrics && (
                  <div className="bg-white border border-slate-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-green-50 rounded-lg flex items-center justify-center">
                        <i className="fas fa-chart-line text-green-500 text-[10px]"></i>
                      </div>
                      <h3 className="text-xs font-bold text-slate-900">Success Metrics</h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[9px] font-medium text-slate-400 uppercase mb-0.5">Track</p>
                        <p className="text-[11px] font-bold text-slate-800">{briefingData.successMetrics.primaryMetric}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[9px] font-medium text-slate-400 uppercase mb-0.5">Target</p>
                        <p className="text-[11px] font-bold text-slate-800">{briefingData.successMetrics.benchmark}</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-2.5 text-center">
                        <p className="text-[9px] font-medium text-slate-400 uppercase mb-0.5">Hook Tip</p>
                        <p className="text-[11px] font-bold text-slate-800">{briefingData.successMetrics.hookTip}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Start Creating Button */}
                <button onClick={() => setActiveSection('content')}
                  className="w-full px-4 py-3.5 bg-slate-900 text-white text-sm font-semibold rounded-xl hover:bg-slate-800 transition-all flex items-center justify-center gap-2">
                  <i className="fas fa-arrow-right"></i>
                  <span>Start Creating Content</span>
                </button>
              </>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <p className="text-sm text-slate-500">Unable to load briefing</p>
                <button onClick={handleLoadBriefing} className="mt-3 text-xs text-slate-700 underline">Try again</button>
              </div>
            )}
          </div>
        )}

        {/* ═══ CONTENT SECTION ═══ */}
        {activeSection === 'content' && (
          <div className="p-5 max-w-3xl mx-auto space-y-5">

            {/* Post Title */}
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1.5">Post Title / Topic</label>
              <input type="text" value={editedContent.postTitle}
                onChange={(e) => setEditedContent(prev => ({ ...prev, postTitle: e.target.value }))}
                placeholder="Enter your post title or main topic..."
                className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 transition-all" />
            </div>

            {/* Tone + Format Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Tone</label>
                <div className="grid grid-cols-3 gap-1.5">
                  {TONE_OPTIONS.map(t => (
                    <button key={t.id} onClick={() => setEditedContent(prev => ({ ...prev, tone: t.id }))}
                      className={`px-2.5 py-2 text-[11px] font-medium rounded-lg border transition-all ${
                        editedContent.tone === t.id ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Format</label>
                <div className="grid grid-cols-2 gap-1.5">
                  {FORMAT_OPTIONS.map(f => (
                    <button key={f.id} onClick={() => setEditedContent(prev => ({ ...prev, format: f.id }))}
                      className={`px-2.5 py-2 text-[11px] font-medium rounded-lg border transition-all flex items-center gap-1.5 ${
                        editedContent.format === f.id ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}>
                      <i className={`${f.icon} text-[10px]`}></i>{f.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Generate Button */}
            <button onClick={handleGenerateContent} disabled={isGenerating || !editedContent.postTitle}
              className="w-full px-4 py-3 bg-slate-900 text-white text-sm font-semibold rounded-lg hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2">
              {isGenerating ? (
                <><i className="fas fa-spinner fa-spin"></i><span>Generating Content...</span></>
              ) : (
                <><i className="fas fa-wand-magic-sparkles"></i><span>Generate Content with AI</span></>
              )}
            </button>

            {/* Generated Content */}
            {editedContent.generatedContent && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">Generated Content</label>
                    <span className="text-[10px] text-slate-400">{editedContent.generatedContent.length} chars</span>
                  </div>
                  <textarea value={editedContent.generatedContent}
                    onChange={(e) => setEditedContent(prev => ({ ...prev, generatedContent: e.target.value }))}
                    rows={8}
                    className="w-full px-3.5 py-2.5 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 leading-relaxed transition-all" />
                </div>

                {/* Caption */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-700">Caption / Hook</label>
                    <button onClick={handleRegenerateCaption} disabled={isRegeneratingCaption}
                      className="text-[11px] text-slate-500 hover:text-slate-700 font-medium disabled:text-slate-300 flex items-center gap-1">
                      <i className={`fas ${isRegeneratingCaption ? 'fa-spinner fa-spin' : 'fa-sync-alt'} text-[9px]`}></i>
                      {isRegeneratingCaption ? 'Regenerating...' : 'Regenerate'}
                    </button>
                  </div>
                  <textarea value={editedContent.caption}
                    onChange={(e) => setEditedContent(prev => ({ ...prev, caption: e.target.value }))}
                    rows={2}
                    placeholder="Short, punchy caption that hooks the reader..."
                    className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:border-slate-500 transition-all" />
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] text-slate-400">
                      {editedContent.caption.length} / {platform.hookLimit} chars
                      {editedContent.caption.length > platform.hookLimit && (
                        <span className="text-red-500 ml-1">(over limit)</span>
                      )}
                    </span>
                    <span className="text-[10px] text-slate-400">Shows before "See more" on {platform.name}</span>
                  </div>
                </div>

                {/* Hashtags */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-semibold text-slate-700">Hashtags</label>
                    <span className="text-[10px] text-slate-400">
                      {editedContent.hashtags.length} / {platform.hashtagMax} max
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {editedContent.hashtags.map((tag, i) => (
                      <button key={i} onClick={() => toggleHashtag(tag)}
                        className="px-2 py-1 text-[11px] font-medium bg-slate-800 text-white rounded-md hover:bg-slate-700 transition-colors flex items-center gap-1">
                        #{tag} <i className="fas fa-times text-[8px] opacity-60"></i>
                      </button>
                    ))}
                    {editedContent.hashtags.length === 0 && (
                      <span className="text-[11px] text-slate-400 italic">No hashtags yet</span>
                    )}
                  </div>
                  {editedContent.suggestedHashtags.length > 0 && (
                    <div className="mb-2">
                      <p className="text-[10px] text-slate-400 mb-1">Suggested:</p>
                      <div className="flex flex-wrap gap-1">
                        {editedContent.suggestedHashtags.filter(t => !editedContent.hashtags.includes(t)).map((tag, i) => (
                          <button key={i} onClick={() => toggleHashtag(tag)}
                            className="px-2 py-0.5 text-[10px] font-medium bg-white border border-slate-200 text-slate-500 rounded-md hover:border-slate-400 transition-colors">
                            + #{tag}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <input type="text" placeholder="Type and press Enter to add..."
                    className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:border-slate-500 transition-all"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value.trim()) {
                        const tag = e.target.value.trim().replace(/^#/, '');
                        if (tag && !editedContent.hashtags.includes(tag)) {
                          setEditedContent(prev => ({ ...prev, hashtags: [...prev.hashtags, tag] }));
                        }
                        e.target.value = '';
                        e.preventDefault();
                      }
                    }} />
                </div>
              </>
            )}

            {/* ═══ CAROUSEL SLIDE IMAGES (shown when format = carousel + content generated) ═══ */}
            {editedContent.format === 'carousel' && editedContent.generatedContent && (() => {
              const slides = parseCarouselSlides(editedContent.generatedContent);
              if (!slides.length) return null;
              return (
                <div className="mt-5 border border-indigo-200 bg-indigo-50/40 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="fas fa-images text-white text-xs"></i>
                    </div>
                    <div>
                      <h4 className="text-xs font-bold text-indigo-900">Carousel Slide Images</h4>
                      <p className="text-[10px] text-indigo-600">{slides.length} slides — generate one background image per slide</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {slides.map((slide) => {
                      const imgUrl = slideImages[slide.index] || editedContent.carouselImages?.[slide.index];
                      const isGen = generatingSlideIdx === slide.index;
                      return (
                        <div key={slide.index} className="flex items-start gap-3 bg-white border border-indigo-100 rounded-lg p-3">
                          {/* Slide thumbnail or placeholder */}
                          <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 flex items-center justify-center">
                            {imgUrl ? (
                              <img src={imgUrl} alt={slide.label} className="w-full h-full object-cover" />
                            ) : (
                              <i className="fas fa-image text-slate-300 text-lg"></i>
                            )}
                          </div>
                          {/* Slide text preview */}
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] font-bold text-indigo-700 mb-0.5">{slide.label}</p>
                            <p className="text-[11px] text-slate-600 leading-snug line-clamp-2">
                              {slide.text.replace(/Slide\s+\d+[:\s]/i, '').slice(0, 120)}
                            </p>
                          </div>
                          {/* Generate button */}
                          <button
                            onClick={() => handleGenerateSlideImage(slide.index, slide.text)}
                            disabled={isGen || generatingSlideIdx !== null}
                            className={`flex-shrink-0 px-3 py-1.5 text-[10px] font-bold rounded-lg flex items-center gap-1.5 transition-all ${
                              imgUrl
                                ? 'bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100'
                                : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {isGen ? (
                              <><i className="fas fa-spinner fa-spin text-[9px]"></i>Gen...</>
                            ) : imgUrl ? (
                              <><i className="fas fa-rotate text-[9px]"></i>Redo</>
                            ) : (
                              <><i className="fas fa-wand-magic-sparkles text-[9px]"></i>Generate</>
                            )}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-[9px] text-indigo-400 mt-3 flex items-center gap-1">
                    <i className="fas fa-info-circle"></i>
                    Each slide gets its own background scene. Brand colours &amp; template applied automatically.
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        {/* ═══ IMAGE SOURCE PICKER — choose AI or Design Studio ═══ */}
        {activeSection === 'editor' && imageMode === null && (
          <div>
            {/* Guidance banner — shown when redirected here from Publish */}
            {pendingPublishAfterDesign && (
              <div className="mx-4 mt-4 px-4 py-3 bg-pink-50 border border-pink-200 rounded-xl flex items-start gap-3">
                <i className="fab fa-instagram text-lg mt-0.5" style={{ color: '#E4405F' }}></i>
                <div>
                  <p className="text-[12px] font-bold text-pink-800">Instagram requires an image</p>
                  <p className="text-[11px] text-pink-600 mt-0.5">
                    Add one below — once you save it you'll be taken straight back to publish.
                  </p>
                </div>
              </div>
            )}
            <ImageSourcePicker
              day={day}
              onChooseAI={() => setImageMode('ai')}
              onChooseStudio={() => setImageMode('design')}
            />
          </div>
        )}

        {/* ═══ AI IMAGE GENERATOR ═══ */}
        {activeSection === 'editor' && imageMode === 'ai' && (
          <AIImageGenerator
            day={day}
            businessData={businessData}
            onBack={() => setImageMode(null)}
            onUseInDesignStudio={(url) => {
              setImagePreviewUrl(url);
              setEditedContent(prev => ({ ...prev, imageUrl: url }));
              setImageMode('design');
            }}
            onApplyToPost={(url) => {
              setImagePreviewUrl(url);
              setEditedContent(prev => ({ ...prev, imageUrl: url }));
              setActiveSection('preview');
              // If redirected from Publish, auto-open publish panel after adding AI image
              if (pendingPublishAfterDesign) {
                setPendingPublishAfterDesign(false);
                setTimeout(() => {
                  setShowPublishPanel(true);
                  loadConnectedAccounts();
                }, 150);
              }
            }}
          />
        )}

        {/* ═══ DESIGN STUDIO ═══ */}
        {activeSection === 'editor' && imageMode === 'design' && (
          <DesignStudio
            day={day}
            businessData={businessData}
            strategyData={strategyData}
            baseImageUrl={imagePreviewUrl}
            onSaveDesign={(designData, exportedImageUrl) => {
              // Only update the preview URL when we have a real URL.
              // Auto-save may pass the base-image URL (fallback) or even null
              // when the canvas is cross-origin tainted — never blank out the preview.
              if (exportedImageUrl) setImagePreviewUrl(exportedImageUrl);
              setEditedContent(prev => ({
                ...prev,
                ...(exportedImageUrl ? { imageUrl: exportedImageUrl } : {}),
                designData,
              }));
              // If user was redirected here from Publish (no image flow), auto-return to publish
              if (pendingPublishAfterDesign && exportedImageUrl) {
                setPendingPublishAfterDesign(false);
                setActiveSection('preview');
                setTimeout(() => {
                  setShowPublishPanel(true);
                  loadConnectedAccounts();
                }, 150);
              }
            }}
            onClose={() => setActiveSection('preview')}
          />
        )}

        {/* ═══ PREVIEW SECTION ═══ */}
        {activeSection === 'preview' && (
          <div className="p-5 max-w-3xl mx-auto space-y-5">

            {/* Platform Preview Card */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              {/* Platform header */}
              <div className="px-4 py-3 flex items-center gap-3 border-b border-slate-100">
                <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: platform.color }}>
                  <i className={`${platform.icon} text-white text-sm`}></i>
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900">{businessData?.businessName || 'Your Business'}</p>
                  <p className="text-[10px] text-slate-400">{platform.name} Post</p>
                </div>
              </div>

              {/* Caption preview */}
              {editedContent.caption && (
                <div className="px-4 pt-3">
                  <p className="text-xs font-semibold text-slate-900">{editedContent.caption}</p>
                  {editedContent.generatedContent.length > 125 && (
                    <span className="text-[10px] text-slate-400 cursor-pointer">... more</span>
                  )}
                </div>
              )}

              {/* Image */}
              {imagePreviewUrl && (
                <div className="mt-2">
                  <img src={imagePreviewUrl} alt="Post" className="w-full h-auto" />
                </div>
              )}

              {/* Content */}
              <div className="px-4 py-3">
                <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">{editedContent.generatedContent}</p>
              </div>

              {/* Hashtags — only render separately if the generatedContent doesn't already contain them */}
              {editedContent.hashtags.length > 0 && !editedContent.generatedContent.includes('#') && (
                <div className="px-4 pb-3">
                  <p className="text-xs text-slate-500">{editedContent.hashtags.map(h => `#${h}`).join(' ')}</p>
                </div>
              )}

              {/* Engagement bar */}
              <div className="px-4 py-2.5 border-t border-slate-100 flex items-center gap-5 text-[11px] text-slate-400">
                <span><i className="far fa-heart mr-1"></i>Like</span>
                <span><i className="far fa-comment mr-1"></i>Comment</span>
                <span><i className="far fa-paper-plane mr-1"></i>Share</span>
                <span><i className="far fa-bookmark mr-1"></i>Save</span>
              </div>
            </div>

            {/* Content Stats */}
            <div className="grid grid-cols-4 gap-3">
              {[
                { label: 'Characters', value: editedContent.generatedContent.length, icon: 'fas fa-align-left', colour: '#3B82F6' },
                { label: 'Paragraphs', value: editedContent.generatedContent.split('\n\n').filter(p => p.trim()).length, icon: 'fas fa-paragraph', colour: '#10B981' },
                { label: 'Hashtags', value: editedContent.hashtags.length, icon: 'fas fa-hashtag', colour: '#F59E0B' },
                { label: 'Hook Length', value: editedContent.caption.length, icon: 'fas fa-bolt', colour: '#8B5CF6' }
              ].map(s => (
                <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
                  <i className={`${s.icon} text-sm mb-1`} style={{ color: s.colour }}></i>
                  <p className="text-lg font-bold text-slate-900">{s.value}</p>
                  <p className="text-[10px] text-slate-400 font-medium">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Platform Warnings */}
            {editedContent.caption.length > platform.hookLimit && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <i className="fas fa-exclamation-triangle text-amber-500 text-xs mt-0.5"></i>
                <div>
                  <p className="text-xs font-semibold text-amber-700">Caption exceeds {platform.name} hook limit</p>
                  <p className="text-[11px] text-amber-600">First {platform.hookLimit} characters show before "See more". Current: {editedContent.caption.length} chars.</p>
                </div>
              </div>
            )}
            {editedContent.hashtags.length > platform.hashtagMax && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
                <i className="fas fa-exclamation-triangle text-amber-500 text-xs mt-0.5"></i>
                <div>
                  <p className="text-xs font-semibold text-amber-700">Too many hashtags for {platform.name}</p>
                  <p className="text-[11px] text-amber-600">Recommended max: {platform.hashtagMax}. Current: {editedContent.hashtags.length}.</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── BOTTOM BAR: Next / Back / Contextual ─── */}
      <div className="flex-shrink-0 border-t border-slate-200 bg-white px-5 py-3 relative">
        <div className="flex items-center justify-between">

          {/* LEFT: Back */}
          <div>
            {activeSection !== 'briefing' && (
              <button
                onClick={() => {
                  const sectionIds = sections.map(s => s.id);
                  const idx = sectionIds.indexOf(activeSection);
                  if (idx > 0) setActiveSection(sectionIds[idx - 1]);
                }}
                className="px-4 py-2 text-[11px] font-semibold text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all flex items-center gap-1.5">
                <i className="fas fa-chevron-left text-[9px]"></i>Back
              </button>
            )}
          </div>

          {/* RIGHT: Preview actions or Next */}
          <div className="flex items-center gap-2">
            {activeSection === 'preview' ? (
              <>
                {/* Auto-save indicator */}
                {autoSaveStatus === 'saving' && (
                  <span className="text-[10px] text-slate-400 flex items-center gap-1">
                    <i className="fas fa-circle-notch fa-spin text-slate-300 text-[9px]"></i>Auto-saving…
                  </span>
                )}
                {autoSaveStatus === 'saved' && (
                  <span className="text-[10px] text-emerald-500 flex items-center gap-1">
                    <i className="fas fa-cloud-check text-emerald-400 text-[9px]"></i>Auto-saved
                  </span>
                )}

                {/* Publish success banner */}
                {publishState === 'success' && publishResult && (
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-300 rounded-lg text-[11px] text-green-800 font-semibold shadow-sm">
                    <i className="fas fa-circle-check text-green-600 text-sm"></i>
                    <span>Published to <span className="capitalize">{publishResult.platform}</span>!</span>
                    {publishResult.postedAt && (
                      <span className="text-[9px] text-green-600 font-normal ml-1">
                        {new Date(publishResult.postedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                )}
                {publishState === 'error' && publishResult?.error && (
                  <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700 font-medium max-w-[300px]">
                    <i className="fas fa-exclamation-circle text-red-500 flex-shrink-0 mt-0.5"></i>
                    <span className="leading-relaxed">{publishResult.error}</span>
                  </div>
                )}

                {/* Copy & Download */}
                <button onClick={handleCopyText} disabled={!editedContent.generatedContent}
                  className="px-3 py-2 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-40 transition-all">
                  <i className="fas fa-copy mr-1.5"></i>Copy
                </button>
                {imagePreviewUrl && (
                  <button onClick={handleDownloadImage}
                    className="px-3 py-2 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all">
                    <i className="fas fa-download mr-1.5"></i>Download
                  </button>
                )}

                {/* Publish dropdown panel */}
                {showPublishPanel && (
                  <div className="absolute bottom-16 right-4 bg-white border border-slate-200 rounded-xl shadow-xl p-3 min-w-[250px] z-50">
                    {(() => {
                      const postPlatform = day.platform;
                      const pConf = PLATFORM_CONFIG[postPlatform] || {};
                      // Server/external URL (safe to share with Meta)
                      const rawImageUrl = imagePreviewUrl && !imagePreviewUrl.startsWith('data:') && !imagePreviewUrl.startsWith('blob:')
                        ? imagePreviewUrl
                        : (editedContent.imageUrl && !editedContent.imageUrl.startsWith('data:') ? editedContent.imageUrl : '');
                      const isLocalUrl = rawImageUrl && (rawImageUrl.includes('localhost') || rawImageUrl.includes('127.0.0.1'));
                      // data:/blob: image exists in preview but hasn't been uploaded to server yet
                      const hasDataUrlImage = !!(
                        (imagePreviewUrl?.startsWith('data:') || imagePreviewUrl?.startsWith('blob:')) ||
                        (editedContent.imageUrl?.startsWith('data:'))
                      );
                      const hasAnyImage = !!(rawImageUrl || hasDataUrlImage);
                      const account = connectedAccounts.find(acc => acc.platform === postPlatform);
                      const isLoading = publishState === 'loading';

                      // ── LOADING ── (panel stays open while posting)
                      if (publishState === 'loading') {
                        return (
                          <div className="py-5 flex flex-col items-center gap-3 text-center">
                            <div className="w-10 h-10 border-[3px] border-purple-200 border-t-purple-600 rounded-full animate-spin"></div>
                            <div>
                              <p className="text-[12px] font-semibold text-slate-700">Publishing to {pConf.name || postPlatform}…</p>
                              {postPlatform === 'instagram' && (
                                <p className="text-[10px] text-slate-400 mt-1">Instagram may take up to 30 seconds to process</p>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // ── SUCCESS ──
                      if (publishState === 'success' && publishResult) {
                        const pCfg = PLATFORM_CONFIG[publishResult.platform] || pConf;
                        return (
                          <div className="py-3 flex flex-col items-center gap-3 text-center">
                            <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center">
                              <i className="fas fa-check text-green-600 text-2xl"></i>
                            </div>
                            <div>
                              <p className="text-[15px] font-extrabold text-slate-800">Posted!</p>
                              <p className="text-[11px] text-slate-500 mt-0.5 capitalize flex items-center justify-center gap-1.5">
                                <i className={`${pCfg.icon || 'fab fa-share-nodes'} text-sm`} style={{ color: pCfg.color }}></i>
                                {publishResult.platform}
                              </p>
                              {publishResult.postedAt && (
                                <p className="text-[10px] text-slate-400 mt-1">
                                  {new Date(publishResult.postedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {' · '}{new Date(publishResult.postedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                                </p>
                              )}
                            </div>
                            <button
                              onClick={() => setShowPublishPanel(false)}
                              className="w-full py-2.5 text-[12px] font-semibold bg-green-600 hover:bg-green-700 text-white rounded-lg transition-all"
                            >
                              <i className="fas fa-check mr-1.5"></i>Done
                            </button>
                          </div>
                        );
                      }

                      // ── ERROR ──
                      if (publishState === 'error' && publishResult?.error) {
                        return (
                          <div className="space-y-3 py-1">
                            <div className="flex flex-col items-center gap-2 text-center">
                              <div className="w-11 h-11 rounded-full bg-red-100 flex items-center justify-center">
                                <i className="fas fa-times text-red-500 text-lg"></i>
                              </div>
                              <p className="text-[12px] font-bold text-red-700">Failed to Post</p>
                              <p className="text-[11px] text-red-600 leading-relaxed">{publishResult.error}</p>
                            </div>
                            <button
                              onClick={() => { setPublishState(null); setPublishResult(null); }}
                              className="w-full py-2 text-[11px] font-semibold border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-all"
                            >
                              <i className="fas fa-redo mr-1"></i>Try Again
                            </button>
                          </div>
                        );
                      }

                      // ── CONFIRMATION SCREEN ──
                      if (confirmPlatform) {
                        const cConf = PLATFORM_CONFIG[confirmPlatform] || {};
                        // "No image at all" — truly nothing
                        const noImageAtAll = confirmPlatform === 'instagram' && !hasAnyImage;
                        // "Has design but not yet on server" — data:/blob: URL
                        const imageNotUploaded = confirmPlatform === 'instagram' && hasDataUrlImage && !rawImageUrl;
                        // "Image on localhost" — can't be reached by Meta
                        const imageIsLocal = !noImageAtAll && !imageNotUploaded && isLocalUrl;
                        const canPost = !noImageAtAll;
                        return (
                          <div className="space-y-3">
                            <div className="text-center pb-1">
                              <div className="w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2" style={{ background: `${cConf.color}18` }}>
                                <i className={`${cConf.icon} text-lg`} style={{ color: cConf.color }}></i>
                              </div>
                              <p className="text-[12px] font-bold text-slate-800">{noImageAtAll ? 'Image Required' : 'Ready to post?'}</p>
                              <p className="text-[10px] text-slate-400 mt-0.5">Posting to <strong>{account?.accountName || cConf.name}</strong></p>
                            </div>

                            {/* Truly no image at all */}
                            {noImageAtAll && (
                              <div className="px-2.5 py-2.5 bg-red-50 border border-red-200 rounded-lg">
                                <p className="text-[11px] text-red-700 font-semibold mb-1">
                                  <i className="fas fa-image mr-1.5 text-red-500"></i>
                                  Instagram requires an image
                                </p>
                                <p className="text-[10px] text-red-600 leading-relaxed">
                                  Go to <strong>Design Studio</strong> to create one, or use <strong>AI Image Generation</strong> in the Content tab.
                                </p>
                              </div>
                            )}

                            {/* Has design but data: URL — will auto-upload then post */}
                            {imageNotUploaded && (
                              <div className="px-2.5 py-2 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-[10px] text-blue-700 leading-relaxed">
                                  <i className="fas fa-cloud-arrow-up mr-1 text-blue-500"></i>
                                  <strong>Image ready</strong> — it will be uploaded automatically when you post. In development the image may not appear on Instagram (localhost), but will work after deployment.
                                </p>
                              </div>
                            )}

                            {/* Localhost server URL — text-only in dev */}
                            {imageIsLocal && (
                              <div className="px-2.5 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-[10px] text-amber-700 leading-relaxed">
                                  <i className="fas fa-image mr-1 text-amber-500"></i>
                                  <strong>Development mode</strong> — image is on localhost, not accessible by Meta. The post will include your caption only. Image will work after deployment.
                                </p>
                              </div>
                            )}

                            {/* Content preview */}
                            {canPost && (
                              <div className="bg-slate-50 rounded-lg px-2.5 py-2 border border-slate-100">
                                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1">Post preview</p>
                                <p className="text-[10px] text-slate-700 line-clamp-3 leading-relaxed">
                                  {editedContent.generatedContent || editedContent.caption || '(no content)'}
                                </p>
                              </div>
                            )}

                            <div className="flex gap-2">
                              <button
                                onClick={() => setConfirmPlatform(null)}
                                className="flex-1 py-2 text-[11px] font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
                              >
                                {noImageAtAll ? 'Back' : 'Cancel'}
                              </button>
                              {canPost && (
                                <button
                                  onClick={() => { handlePublish(confirmPlatform); setConfirmPlatform(null); /* panel stays open to show loading → success */ }}
                                  disabled={isLoading}
                                  className="flex-1 py-2 text-[11px] font-semibold text-white rounded-lg transition-all shadow-sm disabled:opacity-60"
                                  style={{ background: cConf.color || '#7c3aed' }}
                                >
                                  {isLoading
                                    ? <><i className="fas fa-spinner fa-spin mr-1"></i>Posting…</>
                                    : <><i className="fas fa-paper-plane mr-1"></i>Post Now</>
                                  }
                                </button>
                              )}
                              {noImageAtAll && (
                                <button
                                  onClick={() => { setConfirmPlatform(null); setShowPublishPanel(false); setActiveSection('editor'); }}
                                  className="flex-1 py-2 text-[11px] font-semibold text-white rounded-lg transition-all shadow-sm"
                                  style={{ background: cConf.color || '#7c3aed' }}
                                >
                                  <i className="fas fa-wand-magic-sparkles mr-1"></i>Open Design Studio
                                </button>
                              )}
                            </div>
                          </div>
                        );
                      }

                      // ── PLATFORM SELECTION SCREEN ──
                      return (
                        <>
                          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wide mb-2">Post to…</p>
                          {accountsLoading ? (
                            <div className="py-3 flex items-center gap-2 text-[11px] text-slate-500">
                              <i className="fas fa-spinner fa-spin text-slate-400"></i>Loading accounts…
                            </div>
                          ) : !account ? (
                            <div className="py-2 space-y-1">
                              <p className="text-[11px] text-slate-500">
                                <i className="fas fa-plug text-slate-300 mr-1.5"></i>
                                {pConf.name || postPlatform} not connected.
                              </p>
                              <a href="/results" className="text-[11px] text-blue-600 underline block">→ Go to Results to connect</a>
                            </div>
                          ) : account.accountType === 'personal' ? (
                            <div className="py-1 space-y-2">
                              <div className="flex items-center gap-2">
                                <i className={`${pConf.icon} text-base`} style={{ color: pConf.color }}></i>
                                <div>
                                  <p className="text-[11px] font-semibold text-slate-800">{account.accountName}</p>
                                  <p className="text-[9px] text-amber-600 font-medium">Personal account — needs a Page</p>
                                </div>
                              </div>
                              <div className="px-2 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                                <p className="text-[10px] text-amber-800 leading-relaxed">
                                  You need a <strong>Facebook Business Page</strong> to post. Go to <strong>Results → Sync Page</strong>.
                                </p>
                              </div>
                              <a href="/results" className="text-[10px] text-blue-600 underline block text-center">Go to Results → Sync Page</a>
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              <button
                                onClick={() => setConfirmPlatform(postPlatform)}
                                disabled={isLoading}
                                style={{ borderColor: pConf.color || '#334155' }}
                                className="w-full flex items-center gap-2.5 px-3 py-2.5 border-2 rounded-lg text-left transition-all hover:bg-slate-50 disabled:opacity-50"
                              >
                                <i className={`${pConf.icon} text-base`} style={{ color: pConf.color }}></i>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[11px] font-semibold text-slate-800">{pConf.name}</p>
                                  <p className="text-[9px] text-slate-400 truncate">{account.accountName}</p>
                                </div>
                                <i className="fas fa-chevron-right text-[9px] text-slate-300 flex-shrink-0"></i>
                              </button>
                              {/* Inline hints below the button */}
                              {postPlatform === 'instagram' && !hasAnyImage && (
                                <p className="text-[9px] text-amber-600 px-1 leading-relaxed">
                                  <i className="fas fa-exclamation-circle mr-1"></i>
                                  Instagram requires an image — add one in Design Studio first.
                                </p>
                              )}
                              {isLocalUrl && (
                                <p className="text-[9px] text-slate-400 px-1 leading-relaxed">
                                  <i className="fas fa-info-circle mr-1 text-slate-300"></i>
                                  Image on localhost — will post text only. Deploy for image support.
                                </p>
                              )}
                            </div>
                          )}
                          <button
                            onClick={() => setShowPublishPanel(false)}
                            className="mt-3 w-full text-[10px] text-slate-400 hover:text-slate-600 py-1 border-t border-slate-100 pt-2"
                          >
                            Cancel
                          </button>
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Publish button — opens the panel and re-fetches accounts */}
                <div className="relative">
                  <button
                    onClick={() => {
                      // Instagram with no image: skip the panel and go straight to Design Studio
                      const hasImage = !!(imagePreviewUrl || editedContent.imageUrl);
                      if (day.platform === 'instagram' && !hasImage && publishState !== 'success') {
                        setPendingPublishAfterDesign(true);
                        setActiveSection('editor');
                        return;
                      }
                      const next = !showPublishPanel;
                      console.log('[Publish] Button clicked — panel will be:', next ? 'OPEN' : 'CLOSED', '| current accounts:', connectedAccounts.length);
                      setShowPublishPanel(next);
                      // Don't wipe the success state — keeps the button green after publishing
                      if (publishState !== 'success') {
                        setPublishState(null);
                        setPublishResult(null);
                      }
                      if (next) loadConnectedAccounts(); // Only fetch when opening
                    }}
                    className="px-4 py-2 text-[11px] font-semibold text-white rounded-lg transition-all flex items-center gap-1.5 shadow-sm"
                    style={{ background: publishState === 'success' ? '#16a34a' : '#7c3aed' }}
                  >
                    {publishState === 'success' ? (
                      <span className="flex items-center gap-1.5">
                        <i className="fas fa-circle-check"></i>
                        <span>Published!</span>
                        {publishResult?.postedAt && (
                          <span className="text-[9px] font-normal opacity-80">
                            {new Date(publishResult.postedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </span>
                        )}
                      </span>
                    ) : publishState === 'loading' ? (
                      <><i className="fas fa-spinner fa-spin"></i>Publishing…</>
                    ) : (
                      <><i className="fas fa-paper-plane"></i>Publish</>
                    )}
                  </button>
                </div>

                {/* Mark Complete */}
                {publishState !== 'success' && (
                  <button onClick={() => {
                    const safeImageUrl = imagePreviewUrl?.startsWith('data:') ? '' : (imagePreviewUrl || '');
                    const safeVariations = imageVariations.map(v => v.url).filter(u => u && !u.startsWith('data:'));
                    onSave(day.day, {
                      ...editedContent,
                      imageUrl: safeImageUrl,
                      status: 'completed',
                      brief: briefingData,
                      completedAt: new Date().toISOString(),
                      lastEdited: new Date().toISOString(),
                      statusHistory: [...(editedContent.statusHistory || []), { from: editedContent.status, to: 'completed', at: new Date().toISOString() }],
                      selectedVariation,
                      imageVariations: safeVariations,
                    });
                  }}
                    className="px-4 py-2 text-[11px] font-semibold text-white bg-green-600 rounded-lg hover:bg-green-700 transition-all flex items-center gap-1.5">
                    <i className="fas fa-circle-check mr-1"></i>✓ Mark Complete
                  </button>
                )}
                <button onClick={handleSave}
                  className="px-5 py-2 text-[11px] font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-all flex items-center gap-1.5">
                  <i className="fas fa-save"></i>Save
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  const sectionIds = sections.map(s => s.id);
                  const idx = sectionIds.indexOf(activeSection);
                  const nextSec = sectionIds[idx + 1];
                  if (nextSec && !sections[idx + 1]?.disabled) setActiveSection(nextSec);
                }}
                disabled={sections[sections.map(s => s.id).indexOf(activeSection) + 1]?.disabled}
                className="px-5 py-2 text-[11px] font-semibold text-white bg-slate-900 rounded-lg hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-all flex items-center gap-1.5">
                Next<i className="fas fa-chevron-right text-[9px]"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ══ PUBLISH SUCCESS OVERLAY ══
          Full-screen overlay inside the editor — shows a large green tick when a post
          is published successfully. Dismissing it keeps publishState='success' so the
          button stays green and the Published badge remains visible. */}
      {showSuccessOverlay && publishResult && (
        <div className="absolute inset-0 z-[200] flex flex-col items-center justify-center gap-6 rounded-xl"
          style={{ background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(4px)' }}>

          {/* Animated checkmark ring */}
          <div className="relative flex items-center justify-center">
            <div className="w-28 h-28 rounded-full bg-green-50 border-4 border-green-200 flex items-center justify-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center">
                <i className="fas fa-check text-green-600" style={{ fontSize: '2.8rem' }}></i>
              </div>
            </div>
          </div>

          {/* Text */}
          <div className="text-center px-6">
            <p className="text-[26px] font-extrabold text-slate-800 leading-tight">Posted!</p>
            <div className="flex items-center justify-center gap-2 mt-2">
              <i className={`${(PLATFORM_CONFIG[publishResult.platform] || platform).icon} text-lg`}
                 style={{ color: (PLATFORM_CONFIG[publishResult.platform] || platform).color }}></i>
              <span className="text-[14px] font-semibold text-slate-600 capitalize">{publishResult.platform}</span>
            </div>
            {publishResult.postedAt && (
              <p className="text-[12px] text-slate-400 mt-1.5">
                {new Date(publishResult.postedAt).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                {' at '}
                {new Date(publishResult.postedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            )}
          </div>

          {/* Done button */}
          <button
            onClick={() => { setShowSuccessOverlay(false); setShowPublishPanel(false); }}
            className="px-10 py-3 bg-green-600 hover:bg-green-700 text-white text-[13px] font-bold rounded-xl transition-all shadow-lg"
          >
            <i className="fas fa-check mr-2"></i>Done
          </button>
        </div>
      )}
    </div>
  );
}
